/**
 * Responsive design constants and utilities
 * Based on MUI default breakpoints:
 * - xs: 0px (extra-small / mobile)
 * - sm: 600px (small / large mobile)
 * - md: 900px (medium / tablet)
 * - lg: 1200px (large / desktop)
 * - xl: 1536px (extra-large / wide desktop)
 */

// Breakpoint constants matching MUI defaults
export const BREAKPOINTS = {
	XS: 'xs',
	SM: 'sm',
	MD: 'md',
	LG: 'lg',
	XL: 'xl',
} as const;

// Mobile: < 600px (below sm)
export const MOBILE_BREAKPOINT = BREAKPOINTS.SM;

// Tablet: 600px - 900px (sm to md)
export const TABLET_BREAKPOINT = BREAKPOINTS.MD;

// Desktop: >= 900px (md and above)
export const DESKTOP_BREAKPOINT = BREAKPOINTS.MD;

// Mobile DataGrid cell styling
export const MOBILE_CELL_STYLES = {
	GAP: 0.5,
	LINE_HEIGHT: 1.3,
	PADDING: '8px 12px',
	MIN_WIDTH: 220,
} as const;

// Mobile guide text for row click hint
export const MOBILE_GUIDE = {
	TEXT: 'Nhấn để xem chi tiết',
	COLOR: 'primary.main',
	FONT_WEIGHT: 500,
} as const;

// DataGrid row heights
export const ROW_HEIGHTS = {
	MOBILE: 80,
	TABLET: 65,
	DESKTOP: 60,
} as const;

// DataGrid list heights
export const LIST_HEIGHTS = {
	MOBILE: 'calc(100vh - 260px)',
	TABLET: 'calc(100vh - 220px)',
	DESKTOP: 'calc(100vh - 200px)',
} as const;

// Dialog responsive settings
export const DIALOG_SETTINGS = {
	// Confirm dialog (smaller)
	CONFIRM: {
		MAX_WIDTH: 'sm' as const,
	},
	// Form dialog (medium)
	FORM: {
		MAX_WIDTH: 'sm' as const,
	},
	// Detail dialog (larger for tables/data)
	DETAIL: {
		MAX_WIDTH: 'md' as const,
	},
	// Tablet padding (applied via PaperProps.sx)
	TABLET_PADDING: 1,
	// Mobile: fullScreen
	MOBILE: {
		fullScreen: true,
		maxWidth: undefined,
	},
	// Tablet: medium dialog with padding
	TABLET: {
		fullScreen: false,
		maxWidth: 'sm' as const,
		padding: { xs: 2, sm: 3 },
	},
	// Desktop: standard dialog
	DESKTOP: {
		fullScreen: false,
		maxWidth: 'md' as const,
		padding: 3,
	},
} as const;

// Dialog content padding based on screen size
export const DIALOG_CONTENT_PADDING = {
	MOBILE: { px: 2, py: 1.5 },
	TABLET: { px: 3, py: 2 },
	DESKTOP: { px: 3, py: 2 },
} as const;

// Spacing values for responsive layouts
export const RESPONSIVE_SPACING = {
	// Padding for containers
	CONTAINER_PADDING: { xs: 1, sm: 2, md: 3 },
	// Gap between elements
	ELEMENT_GAP: { xs: 1, sm: 1.5, md: 2 },
	// Margin for sections
	SECTION_MARGIN: { xs: 1.5, sm: 2, md: 3 },
} as const;

// DataGrid density by screen size
export const GRID_DENSITY = {
	MOBILE: 'comfortable' as const,
	TABLET: 'standard' as const,
	DESKTOP: 'standard' as const,
} as const;

// Fullscreen dialog paper styles for mobile
// Ensures footer is always visible by using flex layout
export const FULLSCREEN_DIALOG_PAPER_SX = {
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	maxHeight: '100vh',
	overflow: 'hidden',
} as const;

// Dialog content styles for fullscreen mode
// Content should scroll while footer stays fixed
export const FULLSCREEN_DIALOG_CONTENT_SX = {
	flex: 1,
	overflow: 'auto',
	minHeight: 0,
} as const;
